import {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { SharedAccess } from "target_wasm";
import { ShareSuccessContext } from "./ShareSection";
import { RefetchOptions } from "./entities";
import { FetchSharedResult } from "@/components/canvas/detailsSide/tableDetails/TableServices";

export type FetchSharedWithListFn = (
  entityId: string,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  search?: string,
  setContinuationToken?: Dispatch<SetStateAction<string | null>>,
  continuationToken?: string | null,
  entityType?: "users" | "teams",
  limit?: number,
) => Promise<FetchSharedResult | null>;

interface UseSharedAccessManagerOptions {
  entityId: string;
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>;
  fetchFn: FetchSharedWithListFn;
  onPermissionError?: () => Promise<void>;
  onAccessChanged?: () => void;
}

export function useSharedAccessManager(options: UseSharedAccessManagerOptions) {
  const {
    entityId,
    setSharedWithList,
    fetchFn,
    onPermissionError,
    onAccessChanged,
  } = options;

  const [isSuccess, setIsSuccess] = useState(false);
  const [isRevokeAction, setIsRevokeAction] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userContinuationToken, setUserContinuationToken] = useState<
    string | null
  >(null);

  useEffect(() => {
    fetchFn(
      entityId,
      setSharedWithList,
      undefined,
      setUserContinuationToken,
      null,
      "users",
    ).catch(() => {});
  }, [entityId, setSharedWithList, fetchFn]);

  const handleRefetchSharedList = useCallback(
    async (refetchOpts?: RefetchOptions) => {
      await fetchFn(
        entityId,
        setSharedWithList,
        refetchOpts?.search,
        setUserContinuationToken,
        refetchOpts?.continuationToken,
        refetchOpts?.entityType,
        refetchOpts?.limit,
      );
    },
    [entityId, setSharedWithList, fetchFn],
  );

  const handleSuccess = useCallback(
    (context: ShareSuccessContext) => {
      setIsRevokeAction(context.isRevoke);
      setIsSuccess(true);
      setErrorMessage(null);

      if (context.isRevoke) {
        if (userContinuationToken) {
          fetchFn(
            entityId,
            setSharedWithList,
            undefined,
            setUserContinuationToken,
            userContinuationToken,
            "users",
            1,
          );
        }
      } else {
        handleRefetchSharedList({ entityType: "users" });
      }

      onAccessChanged?.();
      setTimeout(() => setIsSuccess(false), 2000);
    },
    [
      handleRefetchSharedList,
      userContinuationToken,
      entityId,
      setSharedWithList,
      fetchFn,
      onAccessChanged,
    ],
  );

  const handleError = useCallback(
    async (error: string, status?: number) => {
      setIsSuccess(false);
      setIsRevokeAction(false);
      setErrorMessage(error);
      setTimeout(() => setErrorMessage(null), 2000);

      if ((status === 401 || status === 403) && onPermissionError) {
        await onPermissionError();
      }
    },
    [onPermissionError],
  );

  return {
    isSuccess,
    isRevokeAction,
    errorMessage,
    userContinuationToken,
    handleRefetchSharedList,
    handleSuccess,
    handleError,
  };
}
